import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema';

import {
	type CommentData as LegacyCommentData,
	type CommentsDataMap as LegacyCommentsDataMap,
	type DataMap,
	type ReplyData as LegacyReplyData,
	CommentActionType,
	CommentType,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData as NewCommentData,
	CommentsDataMap as NewCommentsDataMap,
	ReplyData as NewReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { fg } from '@confluence/feature-gating';
import type {
	ResolvedCommentType,
	ResolvedCommentsQueryType,
	ResolvedInlineCommentLocationType,
} from '@confluence/comments-panel-queries/entry-points/ResolvedCommentsQuery.ts';
import type { CommentsReadMap } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';

type CommentData = LegacyCommentData | NewCommentData;
type ReplyData = LegacyReplyData | NewReplyData;
type CommentOrReplyData = CommentData | ReplyData;

const setCommentProperties = (
	properties: Record<string, any>,
	comments: Record<string, CommentData>,
) =>
	Object.fromEntries(
		Object.entries(comments).map(([id, comment]) => [id, { ...comment, ...properties }]),
	);

export const getResolvedAnnotationState = (unresolvedInlineComments: string[]) =>
	Object.fromEntries(
		unresolvedInlineComments.map((annotationId) => [
			annotationId,
			{
				id: annotationId,
				annotationType: AnnotationTypes.INLINE_COMMENT,
				state: AnnotationMarkStates.RESOLVED,
			},
		]),
	);

const createCommentsDataMap = (
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap,
	propertyUpdate: Record<string, boolean>,
) =>
	Object.entries(commentsDataMap).reduce(
		(updatedCommentsDataMap, [type, comments]) => ({
			...updatedCommentsDataMap,
			[type]: setCommentProperties(propertyUpdate, comments),
		}),
		{} as LegacyCommentsDataMap | NewCommentsDataMap,
	);

const transformCommentsToIdSets = (
	dataMap: DataMap | Record<string, CommentData>,
): Record<string, Set<string>> => {
	return Object.fromEntries(
		Object.entries(dataMap).map(([commentId, comment]) => [
			commentId,
			new Set([
				comment.id,
				...(comment.replies as (LegacyReplyData | NewReplyData)[]).map((reply) => reply.id),
			]),
		]),
	);
};

export const getCommentIdsByTypeAndAnnotationId = (
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap,
): CommentsReadMap => ({
	inline: transformCommentsToIdSets(commentsDataMap.inline),
	general: transformCommentsToIdSets(commentsDataMap.general),
});

export const createReadCommentsDataMap = (
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap,
) => createCommentsDataMap(commentsDataMap, { isUnread: false });

export const combineAllSets = (obj: CommentsReadMap): Set<string> =>
	Object.values(obj).reduce(
		(combinedSet, nestedObj) =>
			new Set([...combinedSet, ...Object.values(nestedObj).flatMap((set) => [...set])]),
		new Set<string>(),
	);

const extractAllComments = (
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap,
): CommentData[] => Object.values(commentsDataMap).flatMap((dataMap) => Object.values(dataMap));

const excludeDraftComments = (comments: CommentData[]): CommentData[] =>
	comments.filter((comment) => comment.contentStatus !== 'draft');

const extractUnreadCommentIds = (comments: CommentOrReplyData[]): string[] =>
	comments
		.filter((comment: CommentOrReplyData | null | undefined) => comment && comment.isUnread)
		.map((comment) => comment.id);

export const computeUnreadCommentIds = (
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap,
	commentsMarkedAsRead: CommentsReadMap,
	includeDrafts: boolean,
): Set<string> => {
	const allParentComments = includeDrafts
		? extractAllComments(commentsDataMap)
		: excludeDraftComments(extractAllComments(commentsDataMap));

	const allCommentReplies = allParentComments.flatMap(
		(comment) => comment.replies as ReplyData[],
	) as ReplyData[];
	const allComments: CommentOrReplyData[] = [...allParentComments, ...allCommentReplies];
	const unreadIds = extractUnreadCommentIds(allComments);

	const readIds = combineAllSets(commentsMarkedAsRead);

	return new Set(unreadIds.filter((id) => !readIds.has(id)));
};

const extractUnresolvedCommentIds = (comments: CommentData[]): string[] =>
	comments.filter((comment) => comment.isOpen).map((comment) => comment.id);

export const computeResolvedCommentIds = (
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap,
	includeDrafts: boolean,
) => {
	const allParentComments = includeDrafts
		? extractAllComments(commentsDataMap)
		: excludeDraftComments(extractAllComments(commentsDataMap));

	return extractUnresolvedCommentIds(allParentComments);
};

const COMMENTS_LOCATIONS = {
	INLINE: 'INLINE',
	FOOTER: 'FOOTER',
};

export const updateResolvedComments = (
	resolvedComments: ResolvedCommentsQueryType,
	commentData: LegacyCommentsDataMap | NewCommentsDataMap,
): LegacyCommentsDataMap | NewCommentsDataMap => {
	if (!resolvedComments?.comments?.nodes) return commentData;
	const resolvedCommentNodes: ResolvedCommentType[] = resolvedComments.comments.nodes.filter(
		(node): node is ResolvedCommentType => node !== null,
	);

	const updateComments = (
		comments: Record<string, any>,
		locationType: string,
		commentType: string,
	) =>
		Object.keys(comments).reduce((acc, key) => {
			const comment = comments[key];
			const resolvedComment = resolvedCommentNodes.find((resolved) =>
				locationType === COMMENTS_LOCATIONS.INLINE
					? (resolved.location as ResolvedInlineCommentLocationType).inlineMarkerRef === key &&
						resolved.location.type === locationType
					: resolved.id === key && resolved.location.type === locationType,
			);

			if (resolvedComment && (comment.isOpen || comment.isUnread)) {
				return {
					...acc,
					[key]: { ...resolvedComment, isOpen: false, isUnread: false, type: commentType },
				};
			} else {
				return {
					...acc,
					[key]: comment,
				};
			}
		}, {});

	const updatedInlineComments = updateComments(
		commentData.inline,
		COMMENTS_LOCATIONS.INLINE,
		CommentType.INLINE,
	);
	const updatedGeneralComments = updateComments(
		commentData.general,
		COMMENTS_LOCATIONS.FOOTER,
		CommentType.GENERAL,
	);

	return {
		inline: updatedInlineComments,
		general: updatedGeneralComments,
	};
};

type DeleteParentCommentParams = {
	isReply: boolean;
	threadKey: string;
	commentType: CommentType;
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	deleteParentComment: (params: { threadKey: string; commentType: CommentType }) => void;
};

export const deleteInlineParentComment = ({
	isReply,
	threadKey,
	commentType,
	commentsDataMap,
	deleteParentComment,
}: DeleteParentCommentParams): LegacyCommentsDataMap | NewCommentsDataMap | null => {
	if (isReply || !fg('confluence_frontend_content_wrapper')) {
		return null;
	}

	const deletedParentComment = {
		[CommentType.INLINE]: { [threadKey]: commentsDataMap.inline[threadKey] },
		[CommentType.GENERAL]: {},
	};

	deleteParentComment({
		threadKey,
		commentType,
	});

	return deletedParentComment as LegacyCommentsDataMap | NewCommentsDataMap;
};

type DeleteParentCommentOptimisticallyParams = {
	threadKey: string;
	commentType: CommentType;
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	deleteParentComment: (params: { threadKey: string; commentType: CommentType }) => void;
};

// works for both inline and general comments
// will replace `deleteInlineParentComment` above
export const deleteParentCommentOptimistically = ({
	threadKey,
	commentType,
	commentsDataMap,
	deleteParentComment,
}: DeleteParentCommentOptimisticallyParams): LegacyCommentsDataMap | NewCommentsDataMap | null => {
	const parentComment = commentsDataMap[commentType][threadKey];
	if (!parentComment) {
		return null;
	}

	const deletedParentComment = {
		[CommentType.INLINE]: commentType === CommentType.INLINE ? { [threadKey]: parentComment } : {},
		[CommentType.GENERAL]:
			commentType === CommentType.GENERAL ? { [threadKey]: parentComment } : {},
	};

	deleteParentComment({
		threadKey,
		commentType,
	});

	return deletedParentComment as LegacyCommentsDataMap | NewCommentsDataMap;
};

type DeleteReplyCommentOptimisticallyParams = {
	threadKey: string;
	commentId: string;
	commentType: CommentType;
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	handleRemovingComments: (params: {
		threadKey: string;
		commentId: string;
		action: CommentActionType;
		commentType: CommentType;
	}) => void;
};

export const deleteReplyCommentOptimistically = ({
	threadKey,
	commentId,
	commentType,
	commentsDataMap,
	handleRemovingComments,
}: DeleteReplyCommentOptimisticallyParams): ReplyData | null => {
	const parentComment = commentsDataMap[commentType][threadKey];
	if (!parentComment) {
		return null;
	}
	const replies = parentComment.replies as LegacyReplyData[] | NewReplyData[];
	const deletedReplyComment = replies.find((reply) => reply.id === commentId);
	if (!deletedReplyComment) {
		return null;
	}

	handleRemovingComments({
		threadKey,
		commentId,
		action: CommentActionType.DELETE_COMMENT,
		commentType,
	});

	return deletedReplyComment;
};
